import React from 'react';
import { motion } from 'framer-motion';
import { RadioIcon, ShieldCheckIcon, ZapIcon, ScalingIcon, ArrowRightIcon, PhoneCallIcon, GlobeIcon, ActivityIcon } from 'lucide-react';
export function SIPTrunksPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div className="bg-white" data-id="element-1842">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-1843">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1844"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1845"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1846">
          <div className="max-w-3xl" data-id="element-1847">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1848">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1849">
                <span className="flex h-2 w-2 rounded-full bg-teal-400 mr-2" data-id="element-1850"></span>
                Advanced VoIP Connectivity
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1851">
                SIP Trunking
                <br data-id="element-1852" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1853">
                  Solutions
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-1854">
                Leverage the power of the internet to connect your existing PBX
                to the public telephone network. SIP Trunks offer a flexible,
                cost-effective alternative to traditional phone lines.
              </p>
              <button onClick={() => onNavigate('contact')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-1855">
                Get a Custom Quote
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1856" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white" data-id="element-1857">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1858">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1859">
            {[{
            title: 'Cost Savings',
            desc: 'Reduce your monthly telecom spend by up to 50% by eliminating expensive PRI and analog lines.',
            icon: ZapIcon,
            color: 'text-teal-500',
            bg: 'bg-teal-50'
          }, {
            title: 'Business Continuity',
            desc: 'Built-in disaster recovery automatically reroutes calls to backup locations or mobile devices if your primary site goes down.',
            icon: ShieldCheckIcon,
            color: 'text-blue-500',
            bg: 'bg-blue-50'
          }, {
            title: 'Scalability',
            desc: 'Easily add or remove channels as your business needs change, without waiting for physical line installations.',
            icon: ScalingIcon,
            color: 'text-purple-500',
            bg: 'bg-purple-50'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="flex flex-col items-start" data-id="element-1860">
                <div className={`w-14 h-14 ${feature.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-1861">
                  <feature.icon className={`w-7 h-7 ${feature.color}`} data-id="element-1862" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1863">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1864">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="py-24 bg-slate-50" data-id="element-1865">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1866">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1867">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1868">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1869">
                Modernize Your Existing PBX
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-1870">
                Don't replace your hardware—enhance it. SIP Trunking allows you
                to keep your current phone system while enjoying the benefits of
                modern VoIP technology.
              </p>
              <div className="space-y-6" data-id="element-1871">
                {[{
                title: 'HD Voice Quality',
                icon: PhoneCallIcon
              }, {
                title: 'Global Number Portability',
                icon: GlobeIcon
              }, {
                title: 'Real-time Monitoring',
                icon: ActivityIcon
              }, {
                title: 'Enterprise-Grade Security',
                icon: ShieldCheckIcon
              }].map((item, i) => <div key={i} className="flex items-center gap-4" data-id="element-1872">
                    <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center" data-id="element-1873">
                      <item.icon className="w-5 h-5 text-teal-600" data-id="element-1874" />
                    </div>
                    <span className="font-bold text-slate-900" data-id="element-1875">
                      {item.title}
                    </span>
                  </div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="rounded-3xl overflow-hidden shadow-2xl" data-id="element-1876">
              <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=600&fit=crop" alt="Server room equipment" className="w-full h-auto" data-id="element-1877" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary" data-id="element-1878">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1879">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1880">
            Ready to switch to SIP Trunking?
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-1881">
            Our experts will help you design a solution that fits your business
            perfectly.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg" data-id="element-1882">
            Contact an Expert
          </button>
        </div>
      </section>
    </div>;
}